// Chemin : C:\gestion-copro\src\services\assistant.service.ts
// Description : Service frontend pour communiquer avec l'Agent SENTINELLE
// Session 95 : Ajout récupération suggestions dynamiques
// Session 96 : Ajout fetchSuggestions() pour suggestions d'accueil (sans message)
// Session 214 : Remontée de agent.courrier dans AssistantResult
// Session 259 : Ajout affaireId optionnel dans AssistantRequest — injection docs dossier côté backend

import { getFunctions, httpsCallable } from 'firebase/functions';
import app from './firebase';
import { 
  ChatRole, 
  AgentResponse, 
  ProposedAction,
  CourrierGenere
} from '../types/chat.types';

// Réponse du backend (correspond à AskAssistantResponse côté functions)
// Session 95 : Ajout champ suggestions
interface BackendResponse {
  success: boolean;
  agent?: AgentResponse;
  message?: string;  // Rétrocompatibilité
  error?: string;
  termesInterditsDetectes?: string[];
  tokensUtilises?: {
    input: number;
    output: number;
    total: number;
  };
  // Session 95 : Suggestions dynamiques
  suggestions?: string[];
}

// Requête vers le backend
interface AssistantRequest {
  message: string;
  conversationHistory: Array<{ role: ChatRole; content: string }>;
  coproprieteId: string;
  /** Session 259 : ID du dossier courant — déclenche l'injection des documents rattachés */
  affaireId?: string;
}

// Session 96 : Types pour getSentinelleSuggestions
interface SuggestionsRequest {
  coproprieteId: string;
}

interface SuggestionsResponse {
  success: boolean;
  suggestions: string[];
  suggestionsBailleur?: string[];
  isBailleur?: boolean;
  error?: string;
}

// Session 170 : Type de retour enrichi pour fetchSuggestions
export interface SuggestionsResult {
  suggestions: string[];
  suggestionsBailleur: string[];
  isBailleur: boolean;
}

// Initialisation Firebase Functions (région Europe)
const functions = getFunctions(app, 'europe-west1');

// Référence à la fonction Cloud
const askAssistantFn = httpsCallable<AssistantRequest, BackendResponse>(
  functions,
  'askAssistant'
);

// Session 96 : Référence à la fonction suggestions d'accueil
const getSentinelleSuggestionsFn = httpsCallable<SuggestionsRequest, SuggestionsResponse>(
  functions,
  'getSentinelleSuggestions'
);

/**
 * Résultat enrichi de l'appel à SENTINELLE
 * Session 95 : Ajout suggestions dynamiques
 */
export interface AssistantResult {
  message: string;
  actions: ProposedAction[];
  incitationPieces?: string;
  // Session 136 : Suggestions de réponse cliquables
  suggestionsReponse?: string[];
  termesInterditsDetectes?: string[];
  tokensUtilises?: {
    input: number;
    output: number;
    total: number;
  };
  // Session 95 : Suggestions dynamiques pour l'écran d'accueil
  suggestions?: string[];
  // Session 214 : Courrier généré (remonte agent.courrier)
  courrier?: CourrierGenere;
}

/**
 * Envoie un message à l'Agent SENTINELLE et retourne sa réponse structurée
 * 
 * @param message - Le message de l'utilisateur
 * @param conversationHistory - L'historique de la conversation
 * @param coproprieteId - L'ID de la copropriété
 * @returns La réponse structurée de SENTINELLE (message + actions + suggestions)
 */
export async function askAssistant(
  message: string,
  conversationHistory: Array<{ role: ChatRole; content: string }>,
  coproprieteId: string,
  affaireId?: string
): Promise<AssistantResult> {
  try {
    const result = await askAssistantFn({
      message,
      conversationHistory,
      coproprieteId,
      ...(affaireId ? { affaireId } : {}),
    });

    if (result.data.success) {
      // Nouvelle réponse Agent
      if (result.data.agent) {
        return {
          message: result.data.agent.message,
          actions: result.data.agent.actions || [],
          incitationPieces: result.data.agent.incitationPieces,
          suggestionsReponse: result.data.agent.suggestionsReponse,
          courrier: result.data.agent.courrier,
          termesInterditsDetectes: result.data.termesInterditsDetectes,
          tokensUtilises: result.data.tokensUtilises,
          // Session 95 : Suggestions dynamiques
          suggestions: result.data.suggestions
        };
      }
      
      // Fallback rétrocompatibilité (ancien format)
      if (result.data.message) {
        return {
          message: result.data.message,
          actions: [],
          termesInterditsDetectes: result.data.termesInterditsDetectes,
          tokensUtilises: result.data.tokensUtilises,
          suggestions: result.data.suggestions
        };
      }
    }

    throw new Error(result.data.error || 'Erreur inconnue de l\'assistant');
  } catch (error: unknown) {
    if (error instanceof Error) {
      console.error('Erreur assistant:', error.message);
      throw new Error(`Impossible de contacter l'assistant : ${error.message}`);
    }
    throw new Error('Erreur inattendue lors de la communication avec l\'assistant');
  }
}

/**
 * Session 96 : Récupère les suggestions d'accueil SENTINELLE
 * Appel léger (Firestore uniquement, zéro Claude, zéro quota)
 * Appelé automatiquement à l'ouverture du chat
 * 
 * @param coproprieteId - L'ID de la copropriété
 * @returns Liste de suggestions textuelles (3 à 5)
 */
export async function fetchSuggestions(coproprieteId: string): Promise<SuggestionsResult> {
  try {
    const result = await getSentinelleSuggestionsFn({ coproprieteId });

    if (result.data.success) {
      return {
        suggestions: result.data.suggestions || [],
        suggestionsBailleur: result.data.suggestionsBailleur || [],
        isBailleur: result.data.isBailleur || false,
      };
    }

    return { suggestions: [], suggestionsBailleur: [], isBailleur: false };
  } catch (error: unknown) {
    console.error('Erreur fetchSuggestions:', error);
    return { suggestions: [], suggestionsBailleur: [], isBailleur: false };
  }
}

/**
 * Version simplifiée qui retourne uniquement le message texte
 * Pour rétrocompatibilité avec le code existant
 * 
 * @deprecated Utiliser askAssistant() qui retourne AssistantResult
 */
export async function askAssistantSimple(
  message: string,
  conversationHistory: Array<{ role: ChatRole; content: string }>,
  coproprieteId: string
): Promise<string> {
  const result = await askAssistant(message, conversationHistory, coproprieteId);
  return result.message;
}
